import React, { useMemo, useEffect, useCallback, useLayoutEffect, useState, useRef } from "react";
import { View, StyleSheet, TextInput, Pressable, Platform, Linking, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { useTheme } from "@/hooks/useTheme";
import { useCurrentEntity } from "@/contexts/CurrentEntityContext";
import { useNavigationHistory } from "@/contexts/NavigationHistoryContext";
import { useEntityEdit } from "@/contexts/EntityEditContext";
import { ContactType } from "@/types/db/contacts";
import { ClientType } from "@/types/db/clients";
import useNavigationGuard from "@/hooks/useNavigationGuard";
import { useNavigation } from "@react-navigation/native";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import Avatar from "@/components/Avatar";
import { useModal } from "@/hooks/useModal";
import { ContactPicker } from "@/components/Pickers/ContactPicker";
import { Table, ColumnDef, RowAction } from "@/components/NewTable";
import { useToolTip } from "@/hooks/useToolTip";

interface ClientContactType {
  contact: {
    id: string;
    first_name?: string;
    last_name?: string;
    preferred_name?: string;
    email_address?: string;
    phone_number?: string;
  };
  note?: string;
}

// Flattened row type for the table
interface ContactRow {
  id: string;
  first_name?: string;
  last_name?: string;
  preferred_name?: string;
  email_address?: string;
  phone_number?: string;
  note?: string;
}

export interface RelatedContactsScreenProps {
  externalAddTrigger?: number;
}

// Tooltip icon button for compact email/phone display
interface TooltipActionButtonProps {
  icon: keyof typeof MaterialCommunityIcons.glyphMap;
  tooltip: string;
  onPress: () => void;
  color: string;
}

function TooltipActionButton({ icon, tooltip, onPress, color }: TooltipActionButtonProps) {
  const { colors } = useTheme();
  const toolTip = useToolTip();
  const ref = useRef<View>(null);
  const isHovering = useRef(false);

  const handlePointerEnter = useCallback(() => {
    isHovering.current = true;
    toolTip.setToolTipContent(
      <View
        style={{
          backgroundColor: colors.background,
          paddingHorizontal: 12,
          paddingVertical: 6,
          borderRadius: 8,
          borderWidth: StyleSheet.hairlineWidth,
          borderColor: colors.border,
        }}
      >
        <ThemedText style={{ fontSize: 13 }}>{tooltip}</ThemedText>
      </View>,
    );
    ref.current?.measureInWindow((x, y) => {
      if (!isHovering.current) return;
      toolTip.setLocation({ x: x, y: y - 36 } as any);
      toolTip.setVisible(true);
    });
  }, [tooltip, colors.background, colors.border, toolTip]);

  const handlePointerLeave = useCallback(() => {
    isHovering.current = false;
    toolTip.setVisible(false);
  }, [toolTip]);

  return (
    <Pressable
      ref={ref}
      onPress={onPress}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={handlePointerLeave}
      style={styles.tooltipButton}
    >
      <MaterialCommunityIcons name={icon} size={18} color={color} />
    </Pressable>
  );
}

export function RelatedContactsScreen({ externalAddTrigger }: RelatedContactsScreenProps) {
  const { colors } = useTheme();
  const currentEntity = useCurrentEntity();
  const { pushNavigation } = useNavigationHistory();
  const { entityData: client, formData, updateFormData, editing } = useEntityEdit<ClientType>();
  const { openModal, closeModal } = useModal();
  const navigation = useNavigation();
  const { navigateToEntity } = useNavigationGuard();
  const [searchValue, setSearchValue] = useState("");

  // Use formData if available, otherwise use entityData
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const clientContacts: ClientContactType[] =
    ((formData as any)?.client_contacts ?? (client as any)?.client_contacts) || [];

  const contactIds = useMemo(() => clientContacts.map((cc) => cc.contact.id), [clientContacts]);

  // Transform to flat rows for the table
  const tableData: ContactRow[] = useMemo(
    () =>
      clientContacts.map((cc) => ({
        id: cc.contact.id,
        first_name: cc.contact.first_name,
        last_name: cc.contact.last_name,
        preferred_name: cc.contact.preferred_name,
        email_address: cc.contact.email_address,
        phone_number: cc.contact.phone_number,
        note: cc.note,
      })),
    [clientContacts],
  );

  const handleContactsSelected = useCallback(
    (contacts: ContactType[]) => {
      const updatedContacts: ClientContactType[] = contacts.map((contact) => {
        // Preserve existing note if contact was already linked
        const existing = clientContacts.find((cc) => cc.contact.id === contact.id);
        return {
          contact: {
            id: contact.id!,
            first_name: contact.first_name || undefined,
            last_name: contact.last_name || undefined,
            preferred_name: contact.meta_data?.preferred_name || undefined,
            email_address: contact.email_address || undefined,
            phone_number: contact.phone_number || undefined,
          },
          note: existing?.note || "",
        };
      });
      updateFormData({ ...formData, client_contacts: updatedContacts } as any);
      closeModal();
    },
    [clientContacts, formData, updateFormData, closeModal],
  );

  const openContactPicker = useCallback(() => {
    openModal(
      <ContactPicker
        title="Manage Contacts"
        pickMany
        selectedContactIds={contactIds}
        onPick={handleContactsSelected}
        onCancel={closeModal}
      />,
    );
  }, [openModal, closeModal, contactIds, handleContactsSelected]);

  // Handle external add trigger from header button
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0) {
      openContactPicker();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalAddTrigger]);

  const handleRemoveContact = useCallback(
    (contactId: string) => {
      const updatedContacts = clientContacts.filter((cc) => cc.contact.id !== contactId);
      updateFormData({ ...formData, client_contacts: updatedContacts } as any);
    },
    [clientContacts, formData, updateFormData],
  );

  const handleContactPress = useCallback(
    (row: ContactRow) => {
      // Save current entity to navigation history before navigating away
      if (currentEntity?.currentPanel && currentEntity?.currentId) {
        pushNavigation({
          panel: currentEntity.currentPanel,
          id: currentEntity.currentId,
          title: currentEntity.currentTitle || currentEntity.currentPanel,
        });
      }

      // Navigate to the contact
      navigateToEntity("contact", row.id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentEntity, pushNavigation],
  );

  const getContactDisplayName = (row: ContactRow) => {
    const firstName = row.preferred_name || row.first_name;
    return `${firstName || ""} ${row.last_name || ""}`.trim() || "—";
  };

  // Set up header with search
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTitle: () => (
        <View style={styles.headerTitleContainer}>
          <ThemedText style={styles.headerTitle}>Contacts</ThemedText>
          <View style={[styles.headerSearchContainer, { backgroundColor: colors.backgroundSecondary }]}>
            <MaterialCommunityIcons
              name="magnify"
              size={16}
              color={colors.textSecondary}
              style={styles.headerSearchIcon}
            />
            <TextInput
              value={searchValue}
              onChangeText={setSearchValue}
              placeholder="Search..."
              placeholderTextColor={colors.textSecondary}
              style={[styles.headerSearchInput, { color: colors.text }]}
              autoCapitalize="none"
              autoCorrect={false}
            />
            {searchValue.length > 0 && (
              <Pressable onPress={() => setSearchValue("")} style={styles.headerClearButton}>
                <MaterialCommunityIcons name="close-circle" size={14} color={colors.textSecondary} />
              </Pressable>
            )}
          </View>
        </View>
      ),
    });
  }, [navigation, searchValue, colors]);

  // Row actions for delete
  const rowActions: RowAction<ContactRow>[] = useMemo(
    () =>
      editing
        ? [
            {
              icon: "trash-can-outline",
              label: "Remove",
              color: colors.red.text,
              onPress: (row) => handleRemoveContact(row.id),
            },
          ]
        : [],
    [editing, colors.red.text, handleRemoveContact],
  );

  // Define columns for the table
  const columns: ColumnDef<ContactRow>[] = useMemo(
    () => [
      {
        key: "avatar",
        header: "",
        width: 48,
        align: "center",
        sortable: false,
        searchable: false,
        render: (_, row) => (
          <Avatar
            email={row.email_address}
            firstName={row.preferred_name || row.first_name}
            lastName={row.last_name}
            style={{ margin: -1 }}
            size={30}
          />
        ),
      },
      {
        key: "name",
        header: "Name",
        flex: 1,
        minWidth: 150,
        accessor: (row) => getContactDisplayName(row),
        onPress: (row) => handleContactPress(row),
      },
      {
        key: "email",
        header: "Email",
        flex: 1,
        minWidth: 180,
        searchable: true,
        searchValue: (_, row) => row.email_address || "",
        render: (_, row) =>
          row.email_address ? (
            <Pressable onPress={() => Linking.openURL(`mailto:${row.email_address}`)} style={styles.contactLink}>
              <MaterialCommunityIcons name="email" size={14} color={colors.primary} />
              <ThemedText style={[styles.contactLinkText, { color: colors.primary }]}>{row.email_address}</ThemedText>
            </Pressable>
          ) : (
            <ThemedText style={{ opacity: 0.3 }}>—</ThemedText>
          ),
      },
      {
        key: "phone",
        header: "Phone",
        width: 140,
        searchable: true,
        searchValue: (_, row) => row.phone_number || "",
        render: (_, row) =>
          row.phone_number ? (
            <Pressable onPress={() => Linking.openURL(`tel:${row.phone_number}`)} style={styles.contactLink}>
              <MaterialCommunityIcons name="phone" size={14} color={colors.primary} />
              <ThemedText style={[styles.contactLinkText, { color: colors.primary }]}>{row.phone_number}</ThemedText>
            </Pressable>
          ) : (
            <ThemedText style={{ opacity: 0.3 }}>—</ThemedText>
          ),
      },
    ],
    [colors, handleContactPress],
  );

  return (
    <ScrollView
      horizontal={true}
      showsHorizontalScrollIndicator={true}
      contentContainerStyle={{ width: "auto", flexGrow: 1 }}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <Table<ContactRow>
        data={tableData}
        columns={columns}
        keyExtractor={(item) => item.id}
        searchValue={searchValue}
        sortable
        defaultSort={{ key: "name", direction: "asc" }}
        emptyMessage="No contacts found"
        emptyIcon="account-outline"
        rowActions={rowActions}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  headerTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  headerTitle: {
    fontSize: 17,
    fontWeight: "600",
  },
  headerSearchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    minWidth: 140,
  },
  headerSearchIcon: {
    marginRight: 6,
  },
  headerSearchInput: {
    flex: 1,
    fontSize: 14,
    minWidth: 100,
    ...Platform.select({
      web: { outlineStyle: "none" as any },
    }),
  },
  headerClearButton: {
    padding: 2,
    marginLeft: 4,
  },
  contactLink: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  contactLinkText: {
    fontSize: 13,
    fontWeight: "500",
  },
  tooltipButton: {
    padding: 4,
    borderRadius: 4,
  },
});
